import {
  Button, InputGroup, InputRow, SecondaryButton, TextInput,
  ToastStatus,
  useToastNotifications,
  useUser,
} from '@equifinance/shared';
import styled from 'styled-components';
import { useEffect, useMemo, useState } from 'react';
import { SectionContainer, SectionHeader } from '../ManageDetailsSection';
import { useBrokerContext } from '../../../contexts/broker';
import useBrokerApi from '../../../api/useBrokerApi';

const EditIcon = styled.i.attrs(() => ({
  className: 'mi-edit',
}))`${({ theme }) => `
  color: ${theme.colours.primary[1]};
  font-size: ${theme.fontSize[3]};
  cursor: pointer;
`}`;

const ButtonGroup = styled.div`${({ theme }) => `
  padding: ${theme.padding[5]};
  display: flex;
  gap: ${theme.padding[2]};
  justify-content: end;
`}`;

export default function PersonalDetailSection() {
  const { brokerDetails, setBrokerDetails } = useBrokerContext();
  const { user, setUser } = useUser();
  const [firstName, setFirstName] = useState(brokerDetails.firstName);
  const [lastName, setLastname] = useState(brokerDetails.surname);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { putUpdateBrokerUser } = useBrokerApi();
  const { addToastNotification } = useToastNotifications();

  const resetData = () => {
    setFirstName(brokerDetails.firstName);
    setLastname(brokerDetails.surname);
  };

  const onClickSave = () => {
    setIsLoading(true);
    const data = brokerDetails;
    putUpdateBrokerUser({
      ...data,
      firstName: firstName.trim(),
      surname: lastName.trim(),
      companyId: user?.companyId || '',
    }).then((res) => {
      setBrokerDetails((prev) => ({
        ...prev,
        ...res,
      }));
      setUser((prev) => ({
        ...prev!,
        firstName: res.firstName,
        lastName: res.surname,
      }));
      setIsEditing(false);
      addToastNotification({
        showCloseIcon: true,
        text: 'Changes have been saved.',
        type: ToastStatus.SUCCESS,
      });
    }).catch(() => {
      addToastNotification({
        text: 'Changes couldn’t be saved. Please try again later.',
        type: ToastStatus.WARNING,
      });
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const disabled = useMemo(() => {
    if (isLoading) return true;
    if (!firstName || !lastName) return true;
    if (firstName.trim() === brokerDetails.firstName && lastName.trim() === brokerDetails.surname) return true;
    return false;
  }, [isLoading, firstName, lastName]);

  useEffect(() => {
    if (!isEditing) {
      resetData();
    }
  }, [
    brokerDetails.firstName,
    brokerDetails.surname,
    isEditing,
  ]);

  return (
    <div>
      <SectionHeader>
        <span>PERSONAL DETAILS</span>
        {!isEditing && (
          <EditIcon onClick={() => setIsEditing(true)} />
        )}
      </SectionHeader>
      <SectionContainer isEditing={isEditing}>
        <InputGroup>
          <InputRow>
            <TextInput
              id="personal-details-first-name"
              value={isEditing ? firstName : brokerDetails.firstName}
              onChange={setFirstName}
              label="First name"
              disabled={!isEditing}
              required
            />
            <TextInput
              id="personal-details-last-name"
              value={isEditing ? lastName : brokerDetails.surname}
              onChange={setLastname}
              label="Last name"
              disabled={!isEditing}
              required
            />
          </InputRow>
        </InputGroup>
        {isEditing && (
          <ButtonGroup>
            <SecondaryButton small onClick={() => setIsEditing(false)}>Cancel</SecondaryButton>
            <Button
              disabled={disabled}
              onClick={onClickSave}
              small
            >
              Save
            </Button>
          </ButtonGroup>
        )}
      </SectionContainer>
    </div>
  );
}
